<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { releaseList, releaseAdd, releaseDel } from "@/common/api/rest/release";

import type { ReleaseType, ReleasePayloadType } from "@/common/types/release";

import { FormRelease } from "@/components";
import {
  breadcrumbSimple,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();

const tableData = ref<ReleaseType[]>([]);
const payload = ref<ReleasePayloadType | null>(null);

const selected = ref<ReleaseType | null>(null);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
]);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "release",
    label: t("release.labels.releases"),
    url: `/register/release`,
  },
]);

const status = computed(() => ({
  ACTIVE: t("common.labels.active"),
  INACTIVE: t("common.labels.inactive"),
}));

function fetchData() {
  releaseList({
    page: page.value,
    limit: limit.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;

  fetchData();
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  releaseAdd(payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        editItem(data);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: ReleaseType) {
  // router.push(`/register/release/${data.release_id}/edit`);
  window.open(`/register/release/${data.release_id}/edit`, "_blank");
}

function removeItem(data: ReleaseType) {
  selected.value = data;
  showModalDel.value = true;
}

function removeItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function removeItemConfirm() {
  if (!selected.value) return;

  releaseDel(selected.value.release_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="release"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <svg-icon icon="no-image" size="2rem" />
        {{ t("release.labels.releases") }}
      </div>

      <div class="tab-content">
        <base-table
          :header="tableHeader"
          :data="tableData"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :del="true"
          :edit="true"
          @change:page="changePage($event)"
          @searching="searchItem($event)"
          @btn:add="addItem()"
          @btn:edit="editItem($event)"
          @btn:del="removeItem($event)"
        >
          <template #status="{ data }">
            {{ status[data.status]?.toUpperCase() }}
          </template>
        </base-table>
      </div>
    </section>
  </article>

  <base-modal
    :show="showModalAdd"
    :title="t('release.modals.titles.add_release').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="addItemCancel()"
    @confirm="addItemConfirm()"
  >
    <template #content>
      <form-release
        @update:data="payload = $event"
        @validate="payloadValid = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalDel"
    :title="t('release.modals.titles.del_release').toUpperCase()"
    :message="
      t('release.modals.messages.del_release', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeItemCancel()"
    @confirm="removeItemConfirm()"
  />
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: inline-flex;
      gap: 0.5rem;
      font-size: 1.2rem;
      align-items: center;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      border-block-end: 1px solid var(--border);
      text-transform: uppercase;
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      padding-inline: 1.5rem;
      padding-block: 1rem;
      gap: 1rem;
    }
  }
}
</style>
